import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              The New Growth Stack
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Decentralizing business ownership beyond products
            </p>
          </div>

          {/* Main Content */}
          <article className="space-y-12">
            
            {/* Problem Statement */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                For decades, owning a business meant giving up ownership. If you sold online, Amazon and Facebook owned your customers. If you sold services, platforms controlled your visibility, pricing, and data.
              </p>
              <p className="text-lg font-medium text-gray-900">
                You were renting distribution, not building relationships.
              </p>
            </section>

            {/* The Klaviyo Example */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">The E-commerce Revolution</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-4">
                Klaviyo broke this cycle for e-commerce. It gave merchants the power to own their customer lists, automate retention, and grow without permission from ad platforms. Suddenly, solo founders could run brands as sophisticated as Nike.
              </p>
              <p className="text-base font-medium text-[#2d4a1e]">
                But what about the rest of the economy?
              </p>
            </section>

            {/* Current State */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">The Service Economy Problem</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                Most entrepreneurs don't sell products—they sell time, expertise, and creativity. From coaches to freelancers, these professionals still live under centralized gatekeepers who control their growth, data, and identity.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Platform Dependency</h3>
                  <p className="text-sm text-gray-600">Rely on marketplaces for clients. Lose your ranking, lose your income.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Data Blindness</h3>
                  <p className="text-sm text-gray-600">Client lists belong to platforms. You never truly know your customers.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Growth Gatekeeping</h3>
                  <p className="text-sm text-gray-600">Expensive ads are the only growth channel, controlled by monopolies.</p>
                </div>
              </div>
            </section>

            {/* The Vision */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">The Solution</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                We believe a new wave of tools is coming that will put client lists, payments, and growth directly in professionals' hands—not platforms.
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2d4a1e] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base text-gray-700">Turn marketing into an owned system, not rented reach</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2d4a1e] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base text-gray-700">Automate growth through AI-native workflows</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#2d4a1e] rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-base text-gray-700">Make individuals the node, not the platform</p>
                </div>
              </div>
              
              <p className="text-base font-medium text-gray-900">
                Just as Shopify + Klaviyo decentralized e-commerce, the next generation will decentralize services.
              </p>
            </section>

            {/* Market Opportunities */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Market Opportunities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Fitness & Wellness</h3>
                    <p className="text-sm text-gray-600">Trainers owning schedules and recurring revenue</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Education & Coaching</h3>
                    <p className="text-sm text-gray-600">Teachers running subscriber networks with AI</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Events & Communities</h3>
                    <p className="text-sm text-gray-600">Organizers engaging audiences directly</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Creative Work</h3>
                    <p className="text-sm text-gray-600">Professionals with storefronts and automated ops</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Healthcare</h3>
                    <p className="text-sm text-gray-600">Independent providers owning patient relationships</p>
                  </div>
                </div>
              </div>
            </section>

            {/* The Shift */}
            <section className="border-t pt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">The Paradigm Shift</h2>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                AI, automation, and embedded finance now allow individuals to run businesses that once required entire teams. The winners won't be platforms who centralize demand, but tools that distribute power back to sellers.
              </p>
              
              <div className="bg-[#2d4a1e] text-white rounded-lg p-6 space-y-3">
                <div className="flex items-center space-x-4">
                  <span className="text-white/70">From</span>
                  <span className="line-through text-white/70">renting growth</span>
                  <span className="text-white/70">→</span>
                  <span className="font-medium">owning it</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-white/70">From</span>
                  <span className="line-through text-white/70">dependency</span>
                  <span className="text-white/70">→</span>
                  <span className="font-medium">autonomy</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-white/70">From</span>
                  <span className="line-through text-white/70">centralized platforms</span>
                  <span className="text-white/70">→</span>
                  <span className="font-medium">decentralized professionals</span>
                </div>
              </div>
            </section>

            {/* Closing Statement */}
            <section className="text-center pt-8">
              <p className="text-2xl font-bold text-gray-900 leading-relaxed">
                When ownership decentralizes,<br />freedom scales.
              </p>
            </section>

          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;